/**
 * Driver Web Push Subscription Helper · v2.7.65+E87
 * ═══════════════════════════════════════════════════════════════════
 * Registers the service worker, requests notification permission, and
 * subscribes to the browser's Push API using the VAPID public key
 * fetched from the backend. Best-effort — silent-fail if the browser
 * doesn't support Web Push or the driver denies permission.
 *
 * Browser detection: Safari (macOS/iOS) supports Web Push only when
 * the site runs as a Home Screen PWA. Regular Safari tabs skip the
 * subscribe flow entirely and let the backend fall back to Twilio SMS
 * on ping_request events.
 */
import api from "./api";

const SW_URL = "/driver-push-sw.js";
const LOCAL_KEY = "rtg_driver_push_subscribed";

// ─── Browser detection ─────────────────────────────────────────────────
export function detectBrowser() {
  if (typeof window === "undefined") return { family: "ssr", isPWA: false, pushSupported: false };
  const ua = navigator.userAgent || "";
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge;
  const isFirefox = /Firefox\//.test(ua);
  const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua);
  const family = isEdge ? "edge" : isChrome ? "chrome" : isFirefox ? "firefox" : isSafari ? "safari" : "other";
  // PWA detection — standalone display mode is set when installed via A2HS.
  const isPWA = (
    window.matchMedia?.("(display-mode: standalone)").matches
    || (navigator.standalone === true)  // iOS Safari legacy flag
    || document.referrer.startsWith("android-app://")
  );
  // Push support matrix:
  //   Chrome/Firefox/Edge: always yes
  //   Safari macOS/iOS:    only when installed as PWA (A2HS) AND
  //                        PushManager is present (macOS 13+ / iOS 16.4+)
  const pushSupported = (
    "serviceWorker" in navigator
    && "PushManager" in window
    && (family !== "safari" || isPWA)
  );
  return { family, isPWA, pushSupported };
}

// ─── VAPID key helper ──────────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const raw = atob((base64String + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

// ─── Subscribe flow ────────────────────────────────────────────────────
/**
 * Called once per session from the driver Dashboard (after the driver
 * taps Go Online, so we know they've engaged with the app). Idempotent
 * — returns the existing subscription if one already exists for this
 * device. Never throws — errors are logged and the driver still gets
 * SMS pings from the backend fallback.
 */
export async function ensureDriverPushSubscription() {
  const meta = detectBrowser();
  const localState = { subscribed: false, ...meta, reason: null };

  if (!meta.pushSupported) {
    localState.reason = meta.family === "safari" && !meta.isPWA
      ? "safari_not_pwa"    // Safari tab — backend falls back to SMS
      : "unsupported";
    return localState;
  }

  try {
    // 1. Register the SW (idempotent — Chrome silently returns the existing one).
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
    await navigator.serviceWorker.ready;

    // 2. If a subscription already exists on this device, no need to prompt.
    let sub = await reg.pushManager.getSubscription();

    // 3. If no subscription yet, ask for permission + subscribe.
    if (!sub) {
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      if (perm !== "granted") {
        localState.reason = "permission_" + perm;
        return localState;
      }
      const { data: keyRes } = await api.get("/drivers/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(keyRes.public_key);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    // 4. Send the subscription up so the backend can push to it.
    const subJson = sub.toJSON();
    await api.post("/drivers/push/subscribe", {
      endpoint:       subJson.endpoint,
      keys:           subJson.keys,
      browser_family: meta.family,
      is_pwa:         meta.isPWA,
      push_supported: meta.pushSupported,
    });
    localStorage.setItem(LOCAL_KEY, "1");
    localState.subscribed = true;
    return localState;
  } catch (e) {
    console.warn("[driver-push] subscribe failed:", e?.message || e);
    localState.reason = "exception:" + (e?.name || "unknown");
    return localState;
  }
}

/** Read-only test hook — never throws. */
export function isDriverPushSubscribed() {
  try { return localStorage.getItem(LOCAL_KEY) === "1"; } catch { return false; }
}
